import { ResizeObserver } from "@juggle/resize-observer";
import { useEffect } from "react";

import type { Form } from "@/react/types/Form";

interface UseAdjustFormHeightProps {
  form?: Form | null;
  index: number;
  isModal: boolean;
}
export const useAdjustFormHeight = ({
  form,
  index,
  isModal,
}: UseAdjustFormHeightProps) => {
  useEffect(() => {
    if (!form || isModal) {
      return;
    }

    const formWrapperId = `form-wrapper-${form.uid}-${index}`;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let timeoutId: number | null = null;

    const attachResizeObserver = (formWrapperElement: HTMLElement) => {
      resizeObserver = new ResizeObserver(() => {
        const wrapperHeight = formWrapperElement.scrollHeight;
        // eslint-disable-next-line sonarjs/post-message -- Disabled for plugin version update. Fix when possible.
        window.parent.postMessage(
          {
            type: "resize",
            formUid: form.uid,
            formIndex: index,
            height: `${wrapperHeight}px`,
          },
          "*",
        );
      });

      resizeObserver.observe(formWrapperElement);
    };

    const existingFormWrapperElement = document.getElementById(formWrapperId);

    // If the element has already been added to the DOM, attach the resize observer immediately
    if (existingFormWrapperElement) {
      attachResizeObserver(existingFormWrapperElement);
    } else {
      // Otherwise, Observe the DOM for changes and attach the resize observer when the element is added.
      mutationObserver = new MutationObserver(() => {
        const formWrapperElement = document.getElementById(formWrapperId);
        if (formWrapperElement) {
          attachResizeObserver(formWrapperElement);
          mutationObserver?.disconnect();
          mutationObserver = null;
        }
      });
      mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      // Disconnect the mutation observer after 2 seconds if the element is not added to the DOM so we don't wait forever.
      timeoutId = window.setTimeout(() => {
        mutationObserver?.disconnect();
        mutationObserver = null;
      }, 2000);
    }
    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [form, index, isModal]);

  return null;
};
